import {
  hasPreservedScreeningReport,
  isInFlightReview,
  isManualReviewerRole,
} from "./screeningScore";

const SUBMITTED_VR_REPORT_VISIBLE_STATUSES = new Set([
  "Submitted",
  "Screening",
  "Completed",
  "Archived",
  "Archieved",
  "Verified",
  "Further Documentation Required",
  "Unverifiable",
  "Completed-Approved",
  "CompletedConditionallyApproved",
  "Completed-Conditionally Approved",
  "Completed-Unacceptable",
]);

export const isSubmittedVrReportVisible = (status: unknown) =>
  SUBMITTED_VR_REPORT_VISIBLE_STATUSES.has(String(status ?? ""));

const IN_PROGRESS_APPLICATION_STATUSES = new Set([
  "applicationinprogress",
  "inprogress",
]);

// Applicant statuses that already have a VR result. A newly added co-applicant
// or co-signer stays Invited and does not.
const SUBMITTED_APPLICANT_RESULT_STATUSES = new Set([
  "submitted",
  "submited",
  "completed",
]);

const normalizeStatusToken = (status: unknown) =>
  String(status ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z]/g, "");

// Result "View" stays for a normal report status. It also stays after a
// co-applicant or co-signer moves the application to In Progress, for an
// applicant who already submitted or who already has a screening score.
export const isExistingVrResultVisible = (
  status: unknown,
  score?: unknown,
  applicantStatus?: unknown,
  hasReviewedReport?: unknown
) => {
  const applicant = normalizeStatusToken(applicantStatus);
  if (applicant === "invited" || applicant === "inprogress") return false;
  return (
    isSubmittedVrReportVisible(status) ||
    hasPreservedScreeningReport(score, status, hasReviewedReport) ||
    (IN_PROGRESS_APPLICATION_STATUSES.has(normalizeStatusToken(status)) &&
      SUBMITTED_APPLICANT_RESULT_STATUSES.has(applicant))
  );
};

export const mapScreeningDisplayStatus = (status: string | undefined) => {
  if (!status) return '';
  return status === 'Screening' ? 'Screened' : status;
};

export const getAtsStatusDisplayLabel = (status: string | undefined) => {
  const statusLabel = mapScreeningDisplayStatus(status);
  if (!statusLabel) return '';
  if (statusLabel === 'CompletedConditionallyApproved') {
    return 'Completed Conditionally Approved';
  }
  if (statusLabel === 'Application In Progress') {
    return 'In Progress';
  }
  if (statusLabel === 'Further Documentation Required') {
    return 'Further doc req.';
  }
  return statusLabel;
};

// Subscriber-facing status label. While the application's system assessments are
// awaiting manual review (Screening / Review Pending), non-reviewers must see
// "Review Pending" rather than a final-looking status such as "Screened" (Story 4622).
export const getSubscriberAtsStatusDisplayLabel = (
  status: string | undefined,
  viewerRole?: string
) =>
  isInFlightReview(status) && !isManualReviewerRole(viewerRole)
    ? "Review Pending"
    : getAtsStatusDisplayLabel(status);

export const getAtsStatusFilterLabel = (status: string | undefined) => {
  const statusLabel = mapScreeningDisplayStatus(status);
  if (!statusLabel) return '';
  if (statusLabel === 'CompletedConditionallyApproved') return 'Completed-Conditionally Approved';
  if (statusLabel === 'Completed-Conditionally Approved') return 'Completed-Conditionally Approved';
  return statusLabel;
};

export const getDocUploadStatusDisplayLabel = (status: string | undefined) => {
  if (!status) return '';
  const normalizedStatus = status.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
  if (normalizedStatus === 'completedapproved') {
    return 'Verified';
  }
  if (normalizedStatus === 'completedconditionallyapproved') {
    return 'Further doc req.';
  }
  if (normalizedStatus === 'completedunacceptable') {
    return 'Unverifiable';
  }
  if (normalizedStatus === 'applicationinprogress') {
    return 'In Progress';
  }
  if (normalizedStatus === 'furtherdocumentationrequired') {
    return 'Further doc req.';
  }
  return status;
};
